/**
 * @author HypnosNova / https://www.threejs.org.cn/gallery
 * This is a class to check whether objects are in a selection area in 3D space
 */

import {
	Camera,
	Frustum,
	Object3D,
	PerspectiveCamera,
	OrthographicCamera,
	Vector3,
	Mesh,
	Line,
	Points,
	Sprite,
} from 'three';

class SelectionBox 
{
	camera: Camera | null;
	scene: Object3D | null;
	startPoint: Vector3;
	endPoint: Vector3;
	collection: Object3D[];
	deep: number;

	// Variables used within the class
	private frustum: Frustum;
	private center: Vector3;

	private tmpPoint: Vector3;

	private vecNear: Vector3;
	private vecTopLeft: Vector3;
	private vecTopRight: Vector3;
	private vecDownRight: Vector3;
	private vecDownLeft: Vector3;

	private vecFarTopLeft: Vector3;
	private vecFarTopRight: Vector3;
	private vecFarDownRight: Vector3;
	private vecFarDownLeft: Vector3;

	private vectemp1: Vector3;
	private vectemp2: Vector3;
	private vectemp3: Vector3;

	constructor(camera: Camera | null, scene: Object3D | null, deep?: number)
	{
		this.camera = camera;
		this.scene = scene;
		this.startPoint = new Vector3();
		this.endPoint = new Vector3();
		this.collection = [];
		this.deep = deep || Number.MAX_VALUE;

		this.frustum = new Frustum();
		this.center = new Vector3();

		this.tmpPoint = new Vector3();

		this.vecNear = new Vector3();
		this.vecTopLeft = new Vector3();
		this.vecTopRight = new Vector3();
		this.vecDownRight = new Vector3();
		this.vecDownLeft = new Vector3();

		this.vecFarTopLeft = new Vector3();
		this.vecFarTopRight = new Vector3();
		this.vecFarDownRight = new Vector3();
		this.vecFarDownLeft = new Vector3();

		this.vectemp1 = new Vector3();
		this.vectemp2 = new Vector3();
		this.vectemp3 = new Vector3();
	}

	setScene(scene: Object3D): void 
	{
		this.scene = scene;
	}

	setCamera(camera: Camera): void 
	{
		this.camera = camera;
	}

	select(startPoint?: Vector3, endPoint?: Vector3): Object3D[]
	{
		this.startPoint = startPoint || this.startPoint;
		this.endPoint = endPoint || this.endPoint;
		this.collection = [];

		this.updateFrustum(this.startPoint, this.endPoint);
		if (this.scene)
		{
			this.searchChildInFrustum(this.frustum, this.scene);
		}

		return this.collection;
	}

	private updateFrustum(startPoint: Vector3, endPoint: Vector3): void 
	{
		if ( this.camera instanceof PerspectiveCamera )
		{
			this.camera.updateProjectionMatrix();
		}

		if ( this.camera )
		{
			this.camera.updateMatrixWorld();
		}

		if (this.camera instanceof PerspectiveCamera) 
		{
			// Perspective Camera
			this.tmpPoint.copy(startPoint);
			this.tmpPoint.x = Math.min(startPoint.x, endPoint.x);
			this.tmpPoint.y = Math.max(startPoint.y, endPoint.y);
			endPoint.x = Math.max(startPoint.x, endPoint.x);
			endPoint.y = Math.min(startPoint.y, endPoint.y);

			this.vecNear.copy(this.camera.position);
			this.vecTopLeft.copy(this.tmpPoint);
			this.vecTopRight.set(endPoint.x, this.tmpPoint.y, 0);
			this.vecDownRight.copy(endPoint);
			this.vecDownLeft.set(this.tmpPoint.x, endPoint.y, 0);

			this.vecTopLeft.unproject(this.camera);
			this.vecTopRight.unproject(this.camera);
			this.vecDownRight.unproject(this.camera);
			this.vecDownLeft.unproject(this.camera);

			this.vectemp1.copy(this.vecTopLeft).sub(this.vecNear);
			this.vectemp2.copy(this.vecTopRight).sub(this.vecNear);
			this.vectemp3.copy(this.vecDownRight).sub(this.vecNear);
			this.vectemp1.normalize();
			this.vectemp2.normalize();
			this.vectemp3.normalize();

			this.vectemp1.multiplyScalar(this.deep);
			this.vectemp2.multiplyScalar(this.deep);
			this.vectemp3.multiplyScalar(this.deep);
			this.vectemp1.add(this.vecNear);
			this.vectemp2.add(this.vecNear);
			this.vectemp3.add(this.vecNear);

			const planes = this.frustum.planes;

			planes[0].setFromCoplanarPoints(this.vecNear, this.vecTopLeft, this.vecTopRight);
			planes[1].setFromCoplanarPoints(this.vecNear, this.vecTopRight, this.vecDownRight);
			planes[2].setFromCoplanarPoints(this.vecDownRight, this.vecDownLeft, this.vecNear);
			planes[3].setFromCoplanarPoints(this.vecDownLeft, this.vecTopLeft, this.vecNear);
			planes[4].setFromCoplanarPoints(this.vecTopRight, this.vecDownRight, this.vecDownLeft);
			planes[5].setFromCoplanarPoints(this.vectemp3, this.vectemp2, this.vectemp1);
			planes[5].normal.multiplyScalar(-1);
		}
		else if (this.camera instanceof OrthographicCamera) 
		{
			// Orthographic Camera
			if (startPoint.equals(endPoint)) endPoint.addScalar(Number.EPSILON); // Avoid invalid frustum

			const left = Math.min(startPoint.x, endPoint.x);
			const top = Math.max(startPoint.y, endPoint.y);
			const right = Math.max(startPoint.x, endPoint.x);
			const down = Math.min(startPoint.y, endPoint.y);

			this.vecTopLeft.set(left, top, -1);
			this.vecTopRight.set(right, top, -1);
			this.vecDownRight.set(right, down, -1);
			this.vecDownLeft.set(left, down, -1);

			this.vecFarTopLeft.set(left, top, 1);
			this.vecFarTopRight.set(right, top, 1);
			this.vecFarDownRight.set(right, down, 1);
			this.vecFarDownLeft.set(left, down, 1);

			this.vecTopLeft.unproject(this.camera);
			this.vecTopRight.unproject(this.camera);
			this.vecDownRight.unproject(this.camera);
			this.vecDownLeft.unproject(this.camera);

			this.vecFarTopLeft.unproject(this.camera);
			this.vecFarTopRight.unproject(this.camera);
			this.vecFarDownRight.unproject(this.camera);
			this.vecFarDownLeft.unproject(this.camera);

			const planes = this.frustum.planes;

			planes[0].setFromCoplanarPoints(this.vecTopLeft, this.vecFarTopLeft, this.vecFarTopRight);
			planes[1].setFromCoplanarPoints(this.vecTopRight, this.vecFarTopRight, this.vecFarDownRight);
			planes[2].setFromCoplanarPoints(this.vecFarDownRight, this.vecFarDownLeft, this.vecDownLeft);
			planes[3].setFromCoplanarPoints(this.vecFarDownLeft, this.vecFarTopLeft, this.vecTopLeft);
			planes[4].setFromCoplanarPoints(this.vecTopRight, this.vecDownRight, this.vecDownLeft);
			planes[5].setFromCoplanarPoints(this.vecFarDownRight, this.vecFarTopRight, this.vecFarTopLeft);
			planes[5].normal.multiplyScalar(-1);
		}
		else 
		{
			console.error('THREE.SelectionBox: Unsupported camera type.');
		}
	}

	private searchChildInFrustum(frustum: Frustum, object: Object3D): void 
	{
		if (object instanceof Mesh || object instanceof Line || object instanceof Points) 
		{
			const geometry = object.geometry;
			const material = object.material;

			if (material !== undefined && geometry !== undefined) 
			{
				if (geometry.boundingSphere === null) geometry.computeBoundingSphere();

				this.center.copy(geometry.boundingSphere.center);
				this.center.applyMatrix4(object.matrixWorld);

				if (frustum.containsPoint(this.center)) 
				{
					this.collection.push(object);
				}
			}
		}
		else if (object instanceof Sprite) 
		{
			const material = object.material;

			if (material !== undefined) 
			{
				this.center.setFromMatrixPosition(object.matrixWorld);

				if (frustum.containsPoint(this.center)) 
				{
					this.collection.push(object);
				}
			}
		}

		for (const child of object.children) 
		{
			this.searchChildInFrustum(frustum, child);
		}
	}
}

export { SelectionBox };
